import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangeFilter, { toInputDate as formatInputDate } from '../components/DateRangeFilter';
import { useAuth } from '../hooks/useAuth';
import { facilityService, equipmentService, reportService, shaService, vendorService } from '../services/dataService';
import { downloadBlob } from '../utils/csv';
import { isRestrictedVendor } from '../utils/permissions';
import styles from './assets/css/ReportsPage.module.css';

const statusOptions = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const pageSizeOptions = [10, 25, 50, 100];

const toApiStartDate = (value) => (value ? new Date(`${value}T00:00:00`).toISOString() : undefined);
const toApiEndDate = (value) => (value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined);

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    maximumFractionDigits: 2,
  }).format(amount);
};

const normalizeStatus = (value) => (value || '').toString().trim().toLowerCase();

const getCompletionLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'in_progress') return 'In progress';
  if (!normalized) return 'Not completed';
  return `Not completed (${normalized.replace(/_/g, ' ')})`;
};

const buildProcedureOptionsFromInterventions = (interventions) => {
  const byCode = new Map();

  (interventions || []).forEach((item) => {
    const code = (item.procedure_code || '').trim();
    if (!code || byCode.has(code)) return;
    byCode.set(code, {
      id: code,
      procedure_code: code,
      name: item.procedure_name || code,
    });
  });

  return Array.from(byCode.values()).sort((a, b) => a.procedure_code.localeCompare(b.procedure_code));
};

const ReportsPage = () => {
  const { user } = useAuth();
  const isVendorUser = isRestrictedVendor(user);
  const scopedVendorId = isVendorUser && user?.vendor_id ? String(user.vendor_id) : '';
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const value = new Date();
    value.setDate(value.getDate() - 30);
    return formatInputDate(value);
  }, []);
  const defaultEnd = useMemo(() => formatInputDate(today), [today]);

  const [filters, setFilters] = useState({
    vendor: scopedVendorId,
    facility: '',
    equipment: '',
    modality: '',
    procedure: '',
    status: '',
    startDate: defaultStart,
    endDate: defaultEnd,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    vendor: scopedVendorId,
    facility: '',
    equipment: '',
    modality: '',
    procedure: '',
    status: '',
    startDate: defaultStart,
    endDate: defaultEnd,
  });
  const [report, setReport] = useState({ items: [], total_cost: 0 });
  const [vendors, setVendors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const loadLookups = useCallback(async () => {
    try {
      const [vendorData, facilityData, equipmentData, interventionData] = await Promise.all([
        vendorService.getAll({ page: 1, page_size: 100 }),
        facilityService.getAll({ page: 1, page_size: 100 }),
        equipmentService.getAll({ page: 1, page_size: 100 }),
        shaService.getInterventions(),
      ]);

      setVendors(vendorData.items || []);
      setFacilities(facilityData.items || []);
      setEquipment(equipmentData.items || []);
      setProcedures(buildProcedureOptionsFromInterventions(interventionData));
    } catch (_) {
      setVendors([]);
      setFacilities([]);
      setEquipment([]);
      setProcedures([]);
    }
  }, []);

  const loadReport = useCallback(async (nextFilters) => {
    const resolvedFilters = nextFilters || appliedFilters;
    setLoading(true);
    setError('');
    try {
      const data = await reportService.getProcedureCostReport({
        vendor: scopedVendorId || resolvedFilters.vendor || undefined,
        facility: resolvedFilters.facility || undefined,
        equipment: resolvedFilters.equipment || undefined,
        modality: resolvedFilters.modality || undefined,
        procedure: resolvedFilters.procedure || undefined,
        status: resolvedFilters.status || undefined,
        start_date: toApiStartDate(resolvedFilters.startDate),
        end_date: toApiEndDate(resolvedFilters.endDate),
      });
      setReport(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load report');
      setReport({ items: [], total_cost: 0 });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, scopedVendorId]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadReport(appliedFilters);
  }, [appliedFilters, loadReport]);

  const modalityOptions = useMemo(
    () => [...new Set(equipment.map((item) => item.category).filter(Boolean))].sort(),
    [equipment],
  );

  const summary = useMemo(() => {
    const items = report.items || [];
    const uniqueVendors = new Set(items.map((item) => item.vendor).filter(Boolean)).size;
    const uniqueFacilities = new Set(items.map((item) => item.facility).filter(Boolean)).size;
    return {
      rows: items.length,
      totalCost: Number(report.total_cost || 0),
      totalVendorRevenue: Number(report.total_vendor_revenue || 0),
      totalFacilityRevenue: Number(report.total_facility_revenue || 0),
      uniqueVendors,
      uniqueFacilities,
    };
  }, [report]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(summary.rows / pageSize)),
    [pageSize, summary.rows],
  );

  const visibleRows = useMemo(() => {
    const items = report.items || [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [page, pageSize, report.items]);

  const pageStart = summary.rows === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, summary.rows);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleApply = async (event) => {
    event.preventDefault();
    setAppliedFilters({
      ...filters,
      vendor: scopedVendorId || filters.vendor,
    });
  };

  const handleReset = async () => {
    const resetFilters = {
      vendor: scopedVendorId,
      facility: '',
      equipment: '',
      modality: '',
      procedure: '',
      status: '',
      startDate: defaultStart,
      endDate: defaultEnd,
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
  };

  const handleDownload = async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await reportService.downloadProcedureCostReportCsv({
        vendor: scopedVendorId || appliedFilters.vendor || undefined,
        facility: appliedFilters.facility || undefined,
        equipment: appliedFilters.equipment || undefined,
        modality: appliedFilters.modality || undefined,
        procedure: appliedFilters.procedure || undefined,
        status: appliedFilters.status || undefined,
        start_date: toApiStartDate(appliedFilters.startDate),
        end_date: toApiEndDate(appliedFilters.endDate),
      });
      downloadBlob(blob, `procedure-cost-report-${appliedFilters.startDate || 'all'}-${appliedFilters.endDate || 'all'}.csv`);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to download CSV report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div>
          <p className={styles.kicker}>Reports</p>
          <h1 className={styles.title}>Procedure cost report</h1>
          <p className={styles.subtitle}>
            Filter procedure activity by vendor, facility, equipment, modality, procedure, status, and date range, then export the result as CSV.
          </p>
        </div>
        <div className={styles.heroActions}>
          <button type="button" className={styles.secondaryButton} onClick={handleReset} disabled={loading || exporting}>
            Reset
          </button>
          <button type="button" className={styles.primaryButton} onClick={handleDownload} disabled={loading || exporting || summary.rows === 0}>
            {exporting ? 'Preparing CSV...' : 'Download CSV'}
          </button>
        </div>
      </div>

      <form className={styles.filters} onSubmit={handleApply}>
        <label>
          <span>Vendor</span>
          <select value={filters.vendor} onChange={handleChange('vendor')} disabled={isVendorUser}>
            {!isVendorUser && <option value="">All vendors</option>}
            {vendors.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Facility</span>
          <select value={filters.facility} onChange={handleChange('facility')}>
            <option value="">All facilities</option>
            {facilities.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Equipment</span>
          <select value={filters.equipment} onChange={handleChange('equipment')}>
            <option value="">All equipment</option>
            {equipment.map((item) => (
              <option key={item.id} value={item.id}>{item.name} ({item.asset_id})</option>
            ))}
          </select>
        </label>

        <label>
          <span>Modality</span>
          <select value={filters.modality} onChange={handleChange('modality')}>
            <option value="">All modalities</option>
            {modalityOptions.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Procedure</span>
          <select value={filters.procedure} onChange={handleChange('procedure')}>
            <option value="">All SHA interventions</option>
            {procedures.map((item) => (
              <option key={item.id} value={item.procedure_code}>{item.procedure_code} - {item.name}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select value={filters.status} onChange={handleChange('status')}>
            <option value="">All statuses</option>
            {statusOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        <div className={styles.dateRangeCell}>
          <DateRangeFilter
            start={filters.startDate}
            end={filters.endDate}
            startLabel="Date start"
            endLabel="Date end"
            onChange={({ start, end }) => setFilters((current) => ({ ...current, startDate: start, endDate: end }))}
          />
        </div>

        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryButton} disabled={loading}>
            {loading ? 'Loading...' : 'Apply filters'}
          </button>
        </div>
      </form>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metrics}>
        <article className={styles.metricCard}>
          <span>Rows</span>
          <strong>{summary.rows}</strong>
        </article>
        <article className={styles.metricCard}>
          <span>Total cost</span>
          <strong>{formatCurrency(summary.totalCost)}</strong>
        </article>
        <article className={styles.metricCard}>
          <span>Vendor revenue</span>
          <strong>{formatCurrency(summary.totalVendorRevenue)}</strong>
        </article>
        <article className={styles.metricCard}>
          <span>Facility revenue</span>
          <strong>{formatCurrency(summary.totalFacilityRevenue)}</strong>
        </article>
        <article className={styles.metricCard}>
          <span>Vendors</span>
          <strong>{summary.uniqueVendors}</strong>
        </article>
        <article className={styles.metricCard}>
          <span>Facilities</span>
          <strong>{summary.uniqueFacilities}</strong>
        </article>
      </section>

      <section className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h2>Procedure rows</h2>
          <span>
            {loading
              ? 'Refreshing report...'
              : `${pageStart}-${pageEnd} of ${summary.rows} result${summary.rows === 1 ? '' : 's'}`}
          </span>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Facility</th>
                <th>Equipment</th>
                <th>Modality</th>
                <th>Patient</th>
                <th>Procedure</th>
                <th>Description</th>
                <th>Equipment use</th>
                <th>Status</th>
                <th>Date</th>
                <th className={styles.rightAlign}>Cost</th>
                <th className={styles.rightAlign}>Vendor revenue</th>
                <th className={styles.rightAlign}>Facility revenue</th>
              </tr>
            </thead>
            <tbody>
              {!loading && summary.rows === 0 && (
                <tr>
                  <td colSpan="13" className={styles.emptyState}>
                    No procedures matched the selected filters.
                  </td>
                </tr>
              )}
              {visibleRows.map((item, index) => (
                <tr key={`${item.equipment_id || item.equipment}-${item.procedure_code}-${item.date}-${index}`}>
                  <td>{item.vendor}</td>
                  <td>{item.facility}</td>
                  <td>{item.equipment}</td>
                  <td>{item.modality}</td>
                  <td>{item.patient_masked_name || '-'}</td>
                  <td>
                    <div className={styles.cellStack}>
                      <strong>{item.procedure}</strong>
                      <span>{item.procedure_code}</span>
                    </div>
                  </td>
                  <td>{item.description || '-'}</td>
                  <td>{item.equipment_use}</td>
                  <td>
                    <div className={styles.cellStack}>
                      <strong>{getCompletionLabel(item.status)}</strong>
                      <span>{normalizeStatus(item.status).replace(/_/g, ' ') || '-'}</span>
                    </div>
                  </td>
                  <td>{formatDateTime(item.date)}</td>
                  <td className={styles.rightAlign}>{formatCurrency(item.cost)}</td>
                  <td className={styles.rightAlign}>
                    <div className={styles.cellStack}>
                      <strong>{formatCurrency(item.vendor_revenue)}</strong>
                      <span>{item.vendor_percentage == null ? '-' : `${Number(item.vendor_percentage).toFixed(2)}%`}</span>
                    </div>
                  </td>
                  <td className={styles.rightAlign}>
                    <div className={styles.cellStack}>
                      <strong>{formatCurrency(item.facility_revenue)}</strong>
                      <span>{item.facility_percentage == null ? '-' : `${Number(item.facility_percentage).toFixed(2)}%`}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <label className={styles.pageSizeControl}>
            <span>Rows per page</span>
            <select value={pageSize} onChange={handlePageSizeChange}>
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          <div className={styles.pageControls}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={loading || page <= 1}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={loading || page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReportsPage;
